import React, { useMemo } from "react";
import ReactFlow, { Background, Controls, MiniMap, MarkerType } from "reactflow";
import "reactflow/dist/style.css";

/**
 * Project 9: nibble - Open Source Go Library
 * Declarative bit-level binary parsing using struct tags (encoding/json style).
 */

const nodeStyle = {
  section: {
    background: "#ffffff",
    border: "2px solid #cbd5e1",
    borderRadius: 12,
    padding: 6,
    fontWeight: 800,
    fontSize: 12,
    color: "#0f172a",
    width: 220,
    textAlign: "center",
  },
  compute: {
    background: "#e7f5ff",
    border: "2px solid #1c7ed6",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0b2e59",
    width: 160,
    textAlign: "center",
  },
  data: {
    background: "#fff0f6",
    border: "2px solid #a61e4d",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#4a0b2a",
    width: 160,
    textAlign: "center",
  },
  ops: {
    background: "#f3fff6",
    border: "2px solid #2f9e44",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#0f3d1f",
    width: 170,
    textAlign: "center",
  },
  note: {
    background: "#fffbeb",
    border: "2px solid #f59e0b",
    borderRadius: 10,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#92400e",
    width: 190,
    textAlign: "center",
  },
  net: {
    background: "#e3fafc",
    border: "2px solid #1098ad",
    borderRadius: 12,
    padding: 6,
    fontWeight: 700,
    fontSize: 12,
    color: "#05333b",
    width: 160,
    textAlign: "center",
  },
};

function mkEdge(id, source, target, label, opts = {}) {
  return {
    id,
    source,
    target,
    label,
    type: "smoothstep",
    animated: opts?.animated ?? false,
    markerEnd: {
      type: MarkerType.ArrowClosed,
      width: 18,
      height: 18,
      color: opts?.style?.stroke ?? "#64748b",
    },
    labelStyle: { fontWeight: 700, fill: "#0f172a" },
    labelBgStyle: { fill: "#ffffff" },
    labelBgPadding: [6, 4],
    style: {
      strokeWidth: 4,
      stroke: "#64748b",
      ...(opts?.style ?? {}),
    },
    ...opts,
  };
}

export default function Project9FlowDiagram() {
  const nodes = useMemo(
    () => [
      // Background groups
      {
        id: "bg-api",
        position: { x: 60, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 600,
          background: "#eff6ff40",
          border: "3px solid #93c5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-engine",
        position: { x: 640, y: 40 },
        data: { label: "" },
        style: {
          width: 540,
          height: 600,
          background: "#ecfdf340",
          border: "3px solid #86efac",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-opt",
        position: { x: 1240, y: 40 },
        data: { label: "" },
        style: {
          width: 520,
          height: 600,
          background: "#fff7ed40",
          border: "3px solid #fdba74",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },
      {
        id: "bg-bench",
        position: { x: 1820, y: 40 },
        data: { label: "" },
        style: {
          width: 460,
          height: 600,
          background: "#f5f3ff40",
          border: "3px solid #c4b5fd",
          borderRadius: 16,
          zIndex: -1,
        },
        draggable: false,
        selectable: false,
      },

      // Section titles
      { id: "sec-api", position: { x: 200, y: 50 }, data: { label: "Public API" }, style: nodeStyle.section },
      { id: "sec-engine", position: { x: 790, y: 50 }, data: { label: "Parser Engine" }, style: nodeStyle.section },
      { id: "sec-opt", position: { x: 1390, y: 50 }, data: { label: "Optimizations" }, style: nodeStyle.section },
      { id: "sec-bench", position: { x: 1940, y: 50 }, data: { label: "Benchmarks" }, style: nodeStyle.section },

      // Public API path
      { id: "n1", position: { x: 200, y: 140 }, data: { label: "User Go App" }, style: nodeStyle.compute },
      { id: "n2", position: { x: 200, y: 230 }, data: { label: "Struct + `nibble:` tags" }, style: nodeStyle.data },
      { id: "n3", position: { x: 200, y: 330 }, data: { label: "nibble.Unmarshal(b, &v)" }, style: nodeStyle.ops },
      { id: "n4", position: { x: 200, y: 430 }, data: { label: "[]byte payload" }, style: nodeStyle.data },
      { id: "n5", position: { x: 200, y: 530 }, data: { label: "Decoded Struct" }, style: nodeStyle.compute },

      // Engine path
      { id: "e1", position: { x: 800, y: 140 }, data: { label: "Type Inspector (reflect)" }, style: nodeStyle.ops },
      { id: "e2", position: { x: 800, y: 230 }, data: { label: "Tag Parser" }, style: nodeStyle.ops },
      { id: "e3", position: { x: 800, y: 330 }, data: { label: "Field Plan Builder" }, style: nodeStyle.ops },
      { id: "e4", position: { x: 800, y: 430 }, data: { label: "Bit-Level Decoder" }, style: nodeStyle.ops },
      { id: "e5", position: { x: 800, y: 530 }, data: { label: "Endianness + Signedness" }, style: nodeStyle.note },

      // Optimizations
      { id: "o1", position: { x: 1380, y: 140 }, data: { label: "Reflection Cache" }, style: nodeStyle.data },
      { id: "o2", position: { x: 1380, y: 230 }, data: { label: "Pre-computed Offsets" }, style: nodeStyle.data },
      { id: "o3", position: { x: 1380, y: 330 }, data: { label: "Zero-Alloc Hot Path" }, style: nodeStyle.data },
      { id: "o4", position: { x: 1380, y: 430 }, data: { label: "Plan Pool (sync.Pool)" }, style: nodeStyle.data },
      { id: "o5", position: { x: 1380, y: 530 }, data: { label: "Inlined Bit Readers" }, style: nodeStyle.data },

      // Benchmarks
      { id: "b1", position: { x: 1880, y: 140 }, data: { label: "182 ns/op Unmarshal" }, style: { ...nodeStyle.note, width: 200 } },
      { id: "b2", position: { x: 1880, y: 240 }, data: { label: "11x faster (reflect cache)" }, style: { ...nodeStyle.note, width: 200 } },
      { id: "b3", position: { x: 1880, y: 340 }, data: { label: "10x vs go-bitfield" }, style: { ...nodeStyle.note, width: 200 } },
      { id: "b4", position: { x: 1880, y: 440 }, data: { label: "5M packets/sec / core" }, style: { ...nodeStyle.note, width: 200 } },
      { id: "b5", position: { x: 1880, y: 540 }, data: { label: "GitHub Pages Bench Report" }, style: { ...nodeStyle.compute, width: 200 } },
    ],
    []
  );

  const edges = useMemo(() => {
    const blue = { stroke: "#2563eb" };
    const green = { stroke: "#16a34a" };
    const orange = { stroke: "#f59e0b" };
    const purple = { stroke: "#7c3aed" };

    return [
      // Public API column
      mkEdge("a1", "n1", "n2", "defines", { style: { ...blue } }),
      mkEdge("a2", "n2", "n3", "call", { style: { ...blue } }),
      mkEdge("a3", "n4", "n3", "input bytes", { style: { ...blue } }),
      mkEdge("a4", "n3", "n5", "decoded", { style: { ...blue } }),

      // API -> Engine
      mkEdge("ae1", "n3", "e1", "reflect type", { style: { ...green } }),

      // Engine column
      mkEdge("g1", "e1", "e2", "fields", { style: { ...green } }),
      mkEdge("g2", "e2", "e3", "bit specs", { style: { ...green } }),
      mkEdge("g3", "e3", "e4", "plan", { style: { ...green } }),
      mkEdge("g4", "e4", "e5", "rules", { style: { ...green } }),
      mkEdge("g5", "e4", "n5", "set fields", { style: { ...green, strokeDasharray: "10 8" } }),

      // Engine <-> Optimizations
      mkEdge("eo1", "e1", "o1", "lookup", { style: { ...orange } }),
      mkEdge("eo2", "e3", "o2", "store offsets", { style: { ...orange } }),
      mkEdge("eo3", "e4", "o3", "reuse buf", { style: { ...orange } }),
      mkEdge("eo4", "e3", "o4", "borrow plan", { style: { ...orange } }),
      mkEdge("eo5", "e4", "o5", "inline reads", { style: { ...orange } }),

      // Optimizations -> Benchmarks (impact)
      mkEdge("ob1", "o1", "b2", "11x", { style: { ...purple } }),
      mkEdge("ob2", "o3", "b1", "182 ns/op", { style: { ...purple } }),
      mkEdge("ob3", "o5", "b4", "5M pkt/s", { style: { ...purple } }),
      mkEdge("ob4", "o2", "b3", "vs go-bitfield", { style: { ...purple } }),
      mkEdge("ob5", "b1", "b5", "publish", { style: { ...purple, strokeDasharray: "10 8" } }),
    ];
  }, []);

  return (
    <div style={{ width: "100%", height: "700px", background: "#ffffff" }}>
      <ReactFlow
        nodes={nodes}
        edges={edges}
        fitView
        fitViewOptions={{ padding: 0.08 }}
        defaultEdgeOptions={{
          type: "smoothstep",
          markerEnd: { type: MarkerType.ArrowClosed },
        }}
        minZoom={0.35}
        maxZoom={2}
        zoomOnScroll={false}
        zoomOnPinch={false}
        panOnScroll={false}
        preventScrolling={true}
      >
        <Background gap={18} size={1} color="#e5e7eb" />
        <MiniMap pannable zoomable />
        <Controls />
      </ReactFlow>
    </div>
  );
}
